import React, { useState, useRef, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import {
  FILESTACK_CDN_WIDTHS,
  filestackCdnUrl,
  pickFilestackHandle,
  pickFilestackHandleHighest
} from "../../../utils/filestackCdn"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily } from "../utils/fontUtils"
import { carouselFrameProps, videoAspectFromElement } from "../utils/galleryVideoAspect"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollReveal, ScrollRevealItem } from "./shared/ScrollReveal"
import { CARD_IMAGE_HOVER_CLASSES } from "./shared/scrollRevealVariants"

const GallerySectionDesktop = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "carousel" // carousel | grid | bento
  const showCaptions = settings.showCaptions || true
  const imageAspectRatio = settings.imageAspectRatio || "auto"
  const enableLightbox = settings.enableLightbox || true
  const lazyLoad = settings.lazyLoad || true
  const [carouselIndex, setCarouselIndex] = useState(0)

  // Get images from section data - support both new and legacy formats
  const images = section.settings?.images || section.images || []

  // Carousel-specific hooks (always declared, even if not used)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [videoAspect, setVideoAspect] = useState(null)
  const videoRef = useRef(null)

  // Bento-specific hooks (always declared, even if not used)
  const [isBentoVideoPlaying, setIsBentoVideoPlaying] = useState(false)
  const bentoVideoRef = useRef(null)

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  // Reset video state when carousel item changes
  useEffect(() => {
    if (layout === "carousel" || !layout) {
      setIsVideoPlaying(false)
      setVideoAspect(null)
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0
      }
    }
  }, [carouselIndex, layout])

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && lightboxOpen) {
        setLightboxOpen(false)
      }
    }
    window.addEventListener("keydown", handleEsc)
    return () => window.removeEventListener("keydown", handleEsc)
  }, [lightboxOpen])

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [lightboxOpen])

  const isVideo = (item) => item?.type === "video" || /\.(mp4|webm|ogg)$/i.test(item?.url || "")

  // Lightbox / zoom: high-res source but CDN-resized (caps Delivery bytes)
  const getHighQualityImageUrl = (image) => {
    if (!image) return null

    const filestackPhoto = image.filestackPhoto || image.filestack_photo || image.photoHandles || image.handles
    const handle = pickFilestackHandleHighest(filestackPhoto)
    if (handle) {
      return filestackCdnUrl(handle, { width: FILESTACK_CDN_WIDTHS.lightbox })
    }

    if (image.url && image.url.includes("cdn.filestackcontent.com")) {
      return filestackCdnUrl(image.url, { width: FILESTACK_CDN_WIDTHS.lightbox }) || image.url
    }

    return image.url
  }

  // Grid / carousel tiles: prefer display derivative + card-width resize
  const getThumbnailUrl = (image) => {
    if (!image) return null

    const filestackPhoto = image.filestackPhoto || image.filestack_photo || image.photoHandles || image.handles
    const handle = pickFilestackHandle(filestackPhoto)
    if (handle) {
      return filestackCdnUrl(handle, { width: FILESTACK_CDN_WIDTHS.gallery })
    }

    return getHighQualityImageUrl(image)
  }

  const openLightbox = (index) => {
    if (enableLightbox && !isVideo(images[index])) {
      setLightboxIndex(index)
      setLightboxOpen(true)
    }
  }

  const closeLightbox = () => setLightboxOpen(false)

  const goToNextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length)
  }

  const goToPrevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const renderMedia = (image, index = 0, explicitSize = null) => {
    if (!image) return null

    const imageUrl = getThumbnailUrl(image)
    if (!imageUrl) return null

    const aspectRatioClass = imageAspectRatio === "square" ? "aspect-square" : "aspect-auto"
    // Always use object-cover to fill containers without empty spaces
    const fitClass = "object-cover"

    return (
      <div
        key={index}
        className={twMerge(
          `group relative overflow-hidden ${aspectRatioClass} ${explicitSize ? explicitSize : ""}`,
          getThemeShapeClasses(theme),
          enableLightbox && !isVideo(image) ? "cursor-pointer" : ""
        )}
        style={{ backgroundColor: "transparent" }}
        onClick={() => enableLightbox && !isVideo(image) && openLightbox(index)}
        role={enableLightbox && !isVideo(image) ? "button" : undefined}
        tabIndex={enableLightbox && !isVideo(image) ? 0 : undefined}
        onKeyDown={(e) => {
          if (enableLightbox && !isVideo(image) && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            openLightbox(index)
          }
        }}>
        {isVideo(image) ? (
          <video
            src={image.url}
            className={`size-full ${fitClass}`}
            preload={lazyLoad ? "metadata" : "auto"}
            controls
          />
        ) : (
          <img
            src={imageUrl}
            alt={image.alt || `Gallery image`}
            className={`size-full ${fitClass} transition-transform duration-300 group-hover:scale-105`}
            loading={lazyLoad ? "lazy" : "eager"}
          />
        )}

        {/* Caption overlay */}
        {showCaptions && image.caption && (
          <div className="absolute inset-x-0 bottom-0 translate-y-full bg-black/50 p-3 text-white transition-transform duration-300 group-hover:translate-y-0">
            <p
              className="text-sm"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph)
              }}>
              {image.caption}
            </p>
          </div>
        )}

        {/* Lightbox overlay */}
        {enableLightbox && !isVideo(image) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-all duration-300 group-hover:bg-black/20">
            <svg
              className="size-8 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>
    )
  }

  const renderCarousel = () => {
    const items = images.length > 0 ? images : []
    const item = items[carouselIndex]

    const go = (delta) =>
      setCarouselIndex((prev) => (items.length === 0 ? 0 : (prev + delta + items.length) % items.length))

    const handleVideoPlay = () => setIsVideoPlaying(true)
    const handleVideoPause = () => setIsVideoPlaying(false)
    const handlePlayButtonClick = (e) => {
      e.stopPropagation()
      if (videoRef.current) {
        videoRef.current.play()
      }
    }
    const handleVideoLoadedMetadata = (e) => {
      setVideoAspect(videoAspectFromElement(e.currentTarget))
    }

    const frame = carouselFrameProps({
      videoAspect: isVideo(item) ? videoAspect : null,
      maxHeightPx: 682,
      maxWidthPx: 1160,
      landscapeClassName: "relative h-[682px] w-[1160px] overflow-hidden bg-black/5"
    })

    return (
      <div className="flex flex-col items-center gap-8 py-24">
        <div className={twMerge(frame.className, getThemeShapeClasses(theme))} style={frame.style}>
          {item ? (
            <div className="flex size-full items-center justify-center">
              {isVideo(item) ? (
                <video
                  ref={videoRef}
                  src={item.url}
                  className="size-full object-contain"
                  preload={lazyLoad ? "metadata" : "auto"}
                  controls
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onEnded={handleVideoPause}
                />
              ) : (
                <img
                  src={getHighQualityImageUrl(item)}
                  alt={item.alt || "Gallery image"}
                  className="size-full object-cover"
                />
              )}
            </div>
          ) : (
            <div className="flex size-full items-center justify-center bg-gray-100">No media</div>
          )}
          {items.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous"
                className="absolute left-8 top-1/2 -translate-y-1/2 rounded-full bg-[rgba(0,0,0,0.4)] p-3 text-white transition-transform hover:scale-105"
                onClick={() => go(-1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next"
                className="absolute right-8 top-1/2 -translate-y-1/2 rounded-full bg-[rgba(0,0,0,0.4)] p-3 text-white transition-transform hover:scale-105"
                onClick={() => go(1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 6l6 6-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}

          {item && isVideo(item) && !isVideoPlaying && (
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              onClick={handlePlayButtonClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handlePlayButtonClick(e)
                }
              }}
              aria-label="Play video">
              <div className="flex size-24 items-center justify-center rounded-full bg-[rgba(0,0,0,0.6)] transition-opacity hover:bg-[rgba(0,0,0,0.8)]">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="9,7 19,12 9,17" />
                </svg>
              </div>
            </div>
          )}
        </div>
        {items.length > 1 && (
          <div className="flex h-3 items-center justify-center gap-2 leading-none">
            {items.map((_, i) => (
              <span
                key={i}
                role="button"
                tabIndex={0}
                className={twMerge(
                  "inline-flex size-2 flex-none cursor-pointer items-center justify-center rounded-full align-middle",
                  i === carouselIndex ? "bg-[#bd654f]" : "bg-gray-300"
                )}
                onClick={() => setCarouselIndex(i)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderBento = () => {
    // Use order in settings.images as given by editor; map to bento slots
    const a = images[0] // left tall
    const e = images[1] // top middle-left
    const b = images[2] // top right
    const c = images[3] // bottom middle-left
    const d = images[4] // bottom right

    const handleBentoVideoPlay = () => setIsBentoVideoPlaying(true)
    const handleBentoVideoPause = () => setIsBentoVideoPlaying(false)
    const handleBentoPlayButtonClick = (e) => {
      e.stopPropagation()
      if (bentoVideoRef.current) {
        bentoVideoRef.current.play()
      }
    }

    return (
      <div className="mx-auto grid h-[559px] w-[1160px] grid-cols-4 grid-rows-2 gap-8">
        <div className="col-span-2 row-span-2">
          {a ? (
            isVideo(a) ? (
              <div className={twMerge("relative size-full overflow-hidden", getThemeShapeClasses(theme))}>
                <video
                  ref={bentoVideoRef}
                  src={a.url}
                  className="size-full object-cover"
                  preload={lazyLoad ? "metadata" : "auto"}
                  controls
                  onPlay={handleBentoVideoPlay}
                  onPause={handleBentoVideoPause}
                  onEnded={handleBentoVideoPause}
                />
                {/* Video overlay positioned to match video size */}
                {!isBentoVideoPlaying && (
                  <div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                    onClick={handleBentoPlayButtonClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        handleBentoPlayButtonClick(e)
                      }
                    }}
                    aria-label="Play video">
                    <div className="flex size-24 items-center justify-center rounded-full bg-[rgba(0,0,0,0.6)] transition-opacity hover:bg-[rgba(0,0,0,0.8)]">
                      <svg width="56" height="56" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                        <polygon points="9,7 19,12 9,17" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              renderMedia(a, 0, "h-full w-full")
            )
          ) : (
            <div className={twMerge("size-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-3 row-start-1">
          {e ? (
            renderMedia(e, 4, "h-full w-full")
          ) : (
            <div className={twMerge("size-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-4 row-start-1">
          {b ? (
            renderMedia(b, 1, "h-full w-full")
          ) : (
            <div className={twMerge("size-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-3 row-start-2">
          {c ? (
            renderMedia(c, 2, "h-full w-full")
          ) : (
            <div className={twMerge("size-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-4 row-start-2">
          {d ? (
            renderMedia(d, 3, "h-full w-full")
          ) : (
            <div className={twMerge("size-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
      </div>
    )
  }

  const renderGrid = () => {
    const items = images.slice(0, 8)
    while (items.length < 8) items.push(null)
    return (
      <div className="mx-auto grid h-[559px] w-[1160px] grid-cols-4 grid-rows-2 gap-8">
        {items.map((it, i) => (
          <ScrollRevealItem key={i} staggerIndex={i + 1} variant="scaleIn" className="group size-full overflow-hidden">
            {it ? (
              renderMedia(it, i, twMerge("size-full", CARD_IMAGE_HOVER_CLASSES))
            ) : (
              <div className={twMerge("size-full bg-gray-100", getThemeShapeClasses(theme))} />
            )}
          </ScrollRevealItem>
        ))}
      </div>
    )
  }

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)

  // Use color scheme for colors
  const sectionBackgroundColor = colorScheme.sectionBackground

  return (
    <>
      <section
        className={twMerge(
          "relative flex min-h-[400px] items-center justify-center py-12",
          isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
        )}
        style={{ backgroundColor: sectionBackgroundColor }}>
        <div className="mx-auto w-full px-0">
          {layout === "bento" && renderBento()}
          {(layout === "grid" || layout === "gallery" || layout === "masonry") && renderGrid()}
          {(layout === "carousel" || !layout) && <ScrollReveal variant="fadeUp">{renderCarousel()}</ScrollReveal>}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && images[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox">
          {/* Close button */}
          <button
            type="button"
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
            onClick={closeLightbox}
            aria-label="Close lightbox">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Navigation arrows - only show if there are multiple images */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation()
                  goToPrevImage()
                }}
                aria-label="Previous image">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation()
                  goToNextImage()
                }}
                aria-label="Next image">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Image container */}
          <div
            className="relative max-h-[90vh] max-w-[90vw]"
            onClick={(e) => e.stopPropagation()}
            role="img"
            aria-label={images[lightboxIndex]?.alt || "Gallery image"}>
            <img
              src={getHighQualityImageUrl(images[lightboxIndex])}
              alt={images[lightboxIndex]?.alt || "Gallery image"}
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
            {/* Caption */}
            {showCaptions && images[lightboxIndex]?.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-black/70 p-4 text-center text-white">
                <p style={{ fontFamily: formatFontFamily(theme?.fonts?.paragraph) }}>{images[lightboxIndex].caption}</p>
              </div>
            )}
          </div>

          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-4 py-2 text-sm text-white">
              {lightboxIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default GallerySectionDesktop
